// ============================================================
// FAMILY TREE COMPONENT - CLEAN LAYOUT WITH CURVED CONNECTORS
// Couples grouped, curved bezier parent-child links, inline expand buttons
// ============================================================

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { usePhotoUrls } from './shared/usePhotoUrl';
import { Icon } from './shared/Icon';

// Support colors for voter nodes
const SC = {
  'Strong Support':  { bg:'var(--sup-strong-bg)',       text:'var(--sup-strong-text)',       dot:'var(--sup-strong-dot)' },
  'Leaning Support': { bg:'var(--sup-lean-bg)',         text:'var(--sup-lean-text)',         dot:'var(--sup-lean-dot)' },
  'Undecided':       { bg:'var(--sup-undecided-bg)',    text:'var(--sup-undecided-text)',    dot:'var(--sup-undecided-dot)' },
  'Leaning Against': { bg:'var(--sup-against-lean-bg)', text:'var(--sup-against-lean-text)', dot:'var(--sup-against-lean-dot)' },
  'Strong Against':  { bg:'var(--sup-against-bg)',      text:'var(--sup-against-text)',      dot:'var(--sup-against-dot)' },
};

// Role generation mapping for tree layout
const ROLE_GEN = {
  // NEW SIMPLIFIED ROLES (3)
  'Grandparent': 1,
  'Parent': 2,
  'Family Member': 3,
  
  // BACKWARD COMPATIBILITY - OLD 56 ROLES
  'Great-Grandfather':0,'Great-Grandmother':0,'Grandfather':1,'Grandmother':1,
  'Father':2,'Mother':2,'Uncle':2,'Aunt':2,
  'Brother':3,'Sister':3,'Me':3,'Husband':3,'Wife':3,'Cousin':3,"Cousin's Husband":3,"Cousin's Wife":3,
  'Son':4,'Daughter':4,'Nephew':4,'Niece':4,'Other':3,
  // Extended roles
  'Paternal Grandfather (Father\'s Father)': 1,
  'Paternal Grandmother (Father\'s Mother)': 1,
  'Maternal Grandfather (Mother\'s Father)': 1,
  'Maternal Grandmother (Mother\'s Mother)': 1,
  'Great-Grandfather (Father\'s Father\'s Father)': 0,
  'Great-Grandmother (Father\'s Father\'s Mother)': 0,
  'Great-Grandfather (Father\'s Mother\'s Father)': 0,
  'Great-Grandmother (Father\'s Mother\'s Mother)': 0,
  'Great-Grandfather (Mother\'s Father\'s Father)': 0,
  'Great-Grandmother (Mother\'s Father\'s Mother)': 0,
  'Great-Grandfather (Mother\'s Mother\'s Father)': 0,
  'Great-Grandmother (Mother\'s Mother\'s Mother)': 0,
  'Father-in-Law': 2,
  'Mother-in-Law': 2,
  'Son-in-Law': 4,
  'Daughter-in-Law': 4,
  'Brother-in-Law (Spouse\'s Brother)': 3,
  'Sister-in-Law (Spouse\'s Sister)': 3,
  'Brother-in-Law (Sibling\'s Husband)': 3,
  'Sister-in-Law (Sibling\'s Wife)': 3,
  'Uncle (Father\'s Brother)': 2,
  'Aunt (Father\'s Sister)': 2,
  'Uncle (Mother\'s Brother)': 2,
  'Aunt (Mother\'s Sister)': 2,
  'Uncle\'s Wife (Father\'s Side)': 2,
  'Aunt\'s Husband (Father\'s Side)': 2,
  'Uncle\'s Wife (Mother\'s Side)': 2,
  'Aunt\'s Husband (Mother\'s Side)': 2,
  'Cousin (Father\'s Side)': 3,
  'Cousin (Mother\'s Side)': 3,
  'Cousin\'s Husband (Father\'s Side)': 3,
  'Cousin\'s Wife (Father\'s Side)': 3,
  'Cousin\'s Husband (Mother\'s Side)': 3,
  'Cousin\'s Wife (Mother\'s Side)': 3,
  'Nephew (Brother\'s Son)': 4,
  'Niece (Brother\'s Daughter)': 4,
  'Nephew (Sister\'s Son)': 4,
  'Niece (Sister\'s Daughter)': 4,
  'Grandson': 5,
  'Granddaughter': 5,
  'Great-Grandson': 6,
  'Great-Granddaughter': 6,
  'Half-Brother (Father\'s Side)': 3,
  'Half-Sister (Father\'s Side)': 3,
  'Half-Brother (Mother\'s Side)': 3,
  'Half-Sister (Mother\'s Side)': 3,
  'Brother\'s Wife': 3,
  'Sister\'s Husband': 3,
};

// Tree layout constants
const NODE_R = 32;
const COUPLE_GAP = 15;  // Tight gap between spouses
const SIBLING_GAP = 50; // Gap between siblings
const FAMILY_GAP = 100; // Gap between different family units
const V_GAP = 80;       // Vertical gap between generations

// ============================================================
// Compute which members are visible, given the viewer and an `expanded`
// set of member IDs whose descendants should also be shown.
//
// Always visible (the "base" tree regardless of expansion):
//   - viewer + viewer's spouse
//   - viewer's ancestors (parents, grandparents, ...) + each one's spouse
//   - viewer's siblings (share a parent with viewer)
//   - viewer's children (kids of viewer), WITHOUT their spouses or kids
//     unless explicitly expanded
//
// Additionally visible when expanded:
//   - for any member in `expanded`, include their spouse and their children
//   - each of those children can themselves be in `expanded`, and the rule
//     applies recursively. "Expand as deep as you want."
//
// The viewer is implicitly expanded (so the viewer's spouse and children
// are always visible), and so are all ancestors and siblings (so the
// direct family always renders). Explicit expansion only matters for
// going DEEPER than the base tree from any node.
// ============================================================
export function getImmediateFamily(allMembers, currentVoterId, expanded = [], fullTree = false) {
  const visible = new Set();
  const expandedSet = new Set(expanded);

  const currentMember = allMembers.find(m => m.voter_id === currentVoterId);
  if (!currentMember) return allMembers;
  
  // Bidirectional spouse lookup — handles one-directional spouse_id in DB.
  const spouseOf = (mem) => {
    if (!mem) return null;
    if (mem.spouse_id) {
      const direct = allMembers.find(x => x.id === mem.spouse_id);
      if (direct) return direct;
    }
    return allMembers.find(x => x.spouse_id === mem.id) || null;
  };
  
  const addWithSpouse = (id) => {
    if (!id) return;
    visible.add(id);
    const mem = allMembers.find(m => m.id === id);
    if (mem) {
      const sp = spouseOf(mem);
      if (sp) visible.add(sp.id);
    }
  };
  
  const childrenOf = (id) => allMembers.filter(m =>
    (m.parent_ids || []).includes(id)
  );
  
  // --- Viewer ---
  addWithSpouse(currentMember.id);
  
  // --- Ancestors ---
  // Immediate: direct parents only.
  // Full Tree: recursive walk up through every generation.
  if (!fullTree) {
    (currentMember.parent_ids || []).forEach(pid => addWithSpouse(pid));
  } else {
    const walkUp = (id, seen = new Set()) => {
      if (seen.has(id)) return;
      seen.add(id);
      const mem = allMembers.find(m => m.id === id);
      if (!mem) return;
      (mem.parent_ids || []).forEach(pid => {
        addWithSpouse(pid);
        walkUp(pid, seen);
      });
    };
    walkUp(currentMember.id);
  }
  
  // --- Siblings (direct — share a parent with viewer) ---
  // These are always visible in the base tree, without their spouses or
  // children unless explicitly expanded.
  const myParents = currentMember.parent_ids || [];
  const siblings = [];
  if (myParents.length > 0) {
    allMembers.forEach(m => {
      if (m.id === currentMember.id) return;
      const theirParents = m.parent_ids || [];
      const sharedParents = myParents.filter(p => theirParents.includes(p));
      if (sharedParents.length > 0) {
        visible.add(m.id);
        siblings.push(m);
      }
    });
  }
  
  // --- Aunts & uncles (Full Tree only): every ancestor's other children. ---
  // Visible as plain nodes; their spouses + kids (cousins) come in via the
  // same +/- expansion mechanism as siblings.
  const auntsUncles = [];
  if (fullTree) {
    const ancestorIds = [...visible].filter(id => id !== currentMember.id);
    const myParentsSet = new Set(myParents);
    const exclude = new Set([currentMember.id, ...siblings.map(s => s.id)]);
    ancestorIds.forEach(aid => {
      childrenOf(aid).forEach(child => {
        // Skip the viewer, the viewer's siblings, and the ancestors
        // themselves — those are already handled. Also skip the chain back
        // down (i.e. the viewer's parents, who are children of grandparents
        // but not aunts/uncles).
        if (exclude.has(child.id)) return;
        if (myParentsSet.has(child.id)) return;
        if (visible.has(child.id)) return;
        visible.add(child.id);
        auntsUncles.push(child);
      });
    });
  }

  // --- Direct children of viewer (always visible, plain nodes) ---
  childrenOf(currentMember.id).forEach(c => {
    visible.add(c.id);
  });
  // Also children of viewer's spouse (same kids, but catches the case where
  // parent_ids has only the spouse listed).
  const viewerSpouse = spouseOf(currentMember);
  if (viewerSpouse) {
    childrenOf(viewerSpouse.id).forEach(c => {
      visible.add(c.id);
    });
  }
  
  // --- Recursive expansion: any member in `expanded` brings in spouse+kids,
  //     and this applies to the new kids too, as deep as they're expanded. ---
  // Candidates to check for expansion: siblings, viewer's children, and
  // then recursively any descendants that get pulled in via expansion.
  const toProcess = [];
  siblings.forEach(s => toProcess.push(s.id));
  childrenOf(currentMember.id).forEach(c => toProcess.push(c.id));
  if (viewerSpouse) {
    childrenOf(viewerSpouse.id).forEach(c => toProcess.push(c.id));
  }
  // Aunts/uncles also expandable in Full Tree, so their cousins can be drilled in.
  auntsUncles.forEach(a => toProcess.push(a.id));
  
  const processed = new Set();
  while (toProcess.length > 0) {
    const id = toProcess.shift();
    if (processed.has(id)) continue;
    processed.add(id);
    if (!expandedSet.has(id)) continue;
    
    // Expanded: pull in this member's spouse + their children.
    const mem = allMembers.find(m => m.id === id);
    if (!mem) continue;
    const sp = spouseOf(mem);
    if (sp) visible.add(sp.id);
    
    const kids = childrenOf(id);
    kids.forEach(k => {
      visible.add(k.id);
      // Queue the kid for possible further expansion.
      toProcess.push(k.id);
    });
    // Also count spouse's children (same kids usually, but defensive).
    if (sp) {
      childrenOf(sp.id).forEach(k => {
        visible.add(k.id);
        toProcess.push(k.id);
      });
    }
  }
  
  return allMembers.filter(m => visible.has(m.id));
}

// Helper: Identify couples (spouse relationships) — handles one-directional spouse_id
function identifyCouples(members) {
  const couples = [];
  const processed = new Set();
  
  members.forEach(m => {
    if (processed.has(m.id)) return;
    
    // Bidirectional lookup: forward link OR someone pointing back at us
    let spouse = null;
    if (m.spouse_id) {
      spouse = members.find(s => s.id === m.spouse_id);
    }
    if (!spouse) {
      spouse = members.find(s => s.spouse_id === m.id);
    }
    
    if (spouse && spouse.id !== m.id) {
      couples.push([m, spouse]);
      processed.add(m.id);
      processed.add(spouse.id);
    }
  });
  
  return couples;
}

// Helper: find a member's spouse in BOTH directions (handles one-directional spouse_id in DB)
function findSpouseBidirectional(member, members) {
  // Forward: this member points at someone
  if (member.spouse_id) {
    const fwd = members.find(s => s.id === member.spouse_id);
    if (fwd) return fwd;
  }
  // Reverse: someone points at this member
  return members.find(s => s.spouse_id === member.id) || null;
}

// Helper: Group members by shared parents (sibling groups)
function identifySiblingGroups(members) {
  const groups = [];
  const processed = new Set();
  
  members.forEach(m => {
    if (processed.has(m.id)) return;
    
    const parentIds = (m.parent_ids || []).sort().join(',');
    if (!parentIds) {
      // No parents - check BIDIRECTIONALLY if they're someone's spouse
      // (handles one-directional spouse_id in the database)
      const partner = findSpouseBidirectional(m, members);
      if (partner && (partner.parent_ids || []).length > 0) {
        // Their partner is a blood relative with a sibling group; let that
        // group pick this member up. Skip for now.
        return;
      }
      if (partner) {
        // Two married-in / orphaned members; pair them as their own group
        groups.push([m, partner]);
        processed.add(m.id);
        processed.add(partner.id);
        return;
      }
      // Not a spouse of anyone, add as single member group
      groups.push([m]);
      processed.add(m.id);
      return;
    }
    
    // Find all siblings with same parents
    const siblings = members.filter(s => {
      if (processed.has(s.id)) return false;
      const sParentIds = (s.parent_ids || []).sort().join(',');
      return sParentIds === parentIds;
    });
    
    if (siblings.length > 0) {
      // Build group with spouses placed immediately next to their partner
      const group = [];
      siblings.forEach(s => {
        processed.add(s.id);
        group.push(s);
        
        // Add spouse immediately after this sibling — BIDIRECTIONAL lookup
        // so that one-directional spouse_id links in the DB still render.
        const spouse = findSpouseBidirectional(s, members);
        if (spouse && !processed.has(spouse.id)) {
          group.push(spouse);
          processed.add(spouse.id);
        }
      });
      
      groups.push(group);
    }
  });
  
  // SAFETY NET: any member that didn't end up in a group (e.g. orphaned
  // married-in spouse whose partner already got grouped via a different path)
  // gets its own single-member group so it still gets a position and renders.
  members.forEach(m => {
    if (!processed.has(m.id)) {
      groups.push([m]);
      processed.add(m.id);
    }
  });
  
  return groups;
}

// Compute the set of member IDs that are in the viewer's bloodline.
// Bloodline = viewer + all ancestors + every descendant of every ancestor
// (which captures siblings, cousins, nieces/nephews, etc.).
//
// CRITICAL: this walk must never cross a spouse edge. If we walked UP from
// a descendant, we'd reach that descendant's OTHER parent (an in-law) and
// through them their whole family. So descent is strictly one-way: from
// ancestor down through children, never using a child's parent_ids to
// bring in more ancestors.
//
// This is RELATIVE to the viewer (highlightId). The same tree rendered from
// a different viewer's profile produces a different bloodline set, which is
// exactly what makes the 💍 ring flip between spouses correctly.
function computeBloodline(allMembers, viewerVoterId) {
  const viewer = allMembers.find(m => m.voter_id === viewerVoterId);
  if (!viewer) return new Set();
  
  const blood = new Set();
  const byId = new Map(allMembers.map(m => [m.id, m]));
  
  // Phase 1: walk UP from the viewer to collect every ancestor.
  const walkUp = (id) => {
    if (blood.has(id)) return;
    blood.add(id);
    const m = byId.get(id);
    if (!m) return;
    (m.parent_ids || []).forEach(pid => walkUp(pid));
  };
  walkUp(viewer.id);
  
  const ancestors = [...blood];
  
  // Phase 2: walk DOWN from every ancestor through children only.
  // A child's parent_ids is NOT followed upward — that would cross
  // into an in-law's family.
  const walkDown = (id) => {
    allMembers.forEach(child => {
      if (blood.has(child.id)) return;
      if ((child.parent_ids || []).includes(id)) {
        blood.add(child.id);
        walkDown(child.id);
      }
    });
  };
  ancestors.forEach(id => walkDown(id));
  
  return blood;
}

// Map each ancestor (parents, grandparents, great-grandparents, etc.) to
// 'paternal' or 'maternal' relative to the viewer. Identifies the viewer's
// father and mother using three layered signals (first hit wins per parent):
//   1. Linked voter's gender — 'Male' on father, 'Female' on mother.
//   2. Family-member role text — 'father' / 'mother' substring (case-insensitive).
//   3. Ancestor's own role text — handles legacy explicit roles like
//      "Paternal Grandfather (Father's Father)".
// Then walks UP from each anchor and tags every reachable ancestor.
// Used by Full Tree to render PATERNAL / MATERNAL badges above ancestor
// couples (the viewer's direct parents are skipped in the render — their
// role label already conveys the side).
function computeAncestorSides(allMembers, viewerVoterId, voters = []) {
  const sides = new Map();
  const viewer = allMembers.find(m => m.voter_id === viewerVoterId);
  if (!viewer) return sides;

  const byId = new Map(allMembers.map(m => [m.id, m]));
  const voterById = new Map(voters.map(v => [v.id, v]));
  const voterFor = (mem) => {
    if (!mem || !mem.voter_id) return null;
    const numId = typeof mem.voter_id === 'string' ? parseInt(mem.voter_id, 10) : mem.voter_id;
    return voterById.get(numId) || voterById.get(mem.voter_id) || null;
  };

  const directParents = (viewer.parent_ids || [])
    .map(pid => byId.get(pid))
    .filter(Boolean);

  // Signal 1 — linked voter gender.
  const byGender = (g) => directParents.find(p => {
    const v = voterFor(p);
    return v && (v.gender || '').toLowerCase() === g;
  });
  let father = byGender('male');
  let mother = byGender('female');

  // Signal 2 — role text on the parent row.
  if (!father) {
    father = directParents.find(p => /father/i.test(p.role || '') && !/-in-law/i.test(p.role || ''));
  }
  if (!mother) {
    mother = directParents.find(p => /mother/i.test(p.role || '') && !/-in-law/i.test(p.role || ''));
  }

  const walkUpTag = (startId, side) => {
    if (!startId) return;
    const visited = new Set();
    const queue = [startId];
    while (queue.length) {
      const id = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);
      sides.set(id, side);
      const m = byId.get(id);
      if (m) (m.parent_ids || []).forEach(pid => queue.push(pid));
    }
  };
  if (father) walkUpTag(father.id, 'paternal');
  if (mother) walkUpTag(mother.id, 'maternal');

  // Signal 3 — fill in any ancestor whose own role explicitly says paternal /
  // maternal (legacy data). Doesn't overwrite earlier tags.
  allMembers.forEach(m => {
    if (sides.has(m.id)) return;
    const role = (m.role || '').toLowerCase();
    if (role.includes('paternal')) sides.set(m.id, 'paternal');
    else if (role.includes('maternal')) sides.set(m.id, 'maternal');
  });

  return sides;
}

// Helper: Decide whether a given member should display the 💍 ring, given the
// viewer's bloodline. A member gets the ring iff they are NOT in the viewer's
// bloodline AND their spouse IS. This flips the ring to the correct side
// depending on whose profile is open.
function isMarriedIn(member, allMembers, bloodline) {
  // Blood relatives never get the ring.
  if (bloodline.has(member.id)) return false;
  
  // Find their spouse — BIDIRECTIONAL (handles one-directional spouse_id in DB)
  let spouse = null;
  if (member.spouse_id) {
    spouse = allMembers.find(m => m.id === member.spouse_id);
  }
  if (!spouse) {
    spouse = allMembers.find(m => m.spouse_id === member.id);
  }
  if (!spouse) return false;
  
  // Ring only if the spouse is a blood relative of the viewer.
  return bloodline.has(spouse.id);
}

// Calculate generation from parent_ids (recursive)
function calculateGeneration(member, allMembers, memo = {}, calculating = new Set()) {
  // Use memoization to avoid recalculating
  if (memo[member.id] !== undefined) return memo[member.id];
  
  // Avoid infinite recursion
  if (calculating.has(member.id)) {
    return 1; // Default to generation 1 if circular
  }
  
  calculating.add(member.id);
  
  // Check if member has parents
  if (member.parent_ids && member.parent_ids.length > 0) {
    const parents = allMembers.filter(m => member.parent_ids.includes(m.id));
    if (parents.length > 0) {
      // Generation = max(parent generations) + 1
      const parentGens = parents.map(p => calculateGeneration(p, allMembers, memo, calculating));
      const gen = Math.max(...parentGens) + 1;
      memo[member.id] = gen;
      calculating.delete(member.id);
      return gen;
    }
  }
  
  // No parents - check if they have a spouse with parents
  // This ensures spouses are always on the same horizontal level
  // BIDIRECTIONAL: handles one-directional spouse_id in DB
  let spouse = null;
  if (member.spouse_id) {
    spouse = allMembers.find(m => m.id === member.spouse_id);
  }
  if (!spouse) {
    spouse = allMembers.find(m => m.spouse_id === member.id);
  }
  if (spouse && spouse.id !== member.id) {
    // Only use spouse's generation if spouse has parents (to avoid infinite loop)
    if (spouse.parent_ids && spouse.parent_ids.length > 0) {
      const spouseGen = calculateGeneration(spouse, allMembers, memo, calculating);
      memo[member.id] = spouseGen;
      calculating.delete(member.id);
      return spouseGen;
    }
  }
  
  // No parents and (no spouse or spouse also has no parents) = top generation (1)
  memo[member.id] = 1;
  calculating.delete(member.id);
  return 1;
}

// ============================================================
// SUBTREE-BASED LAYOUT
//
// The previous algorithm placed every generation left-to-right in
// iteration order and then tried to recenter parents over children as a
// post-process. That works locally but breaks globally: with one parent
// having 1 child and another having 3, the post-process drags parents
// into positions that collide with their neighbors, and the whole tree
// piles up at the right edge.
//
// This replacement is top-down and subtree-based. Each unit (person or
// couple) computes a subtree width that's the max of its own unit width
// and the sum of its children's subtree widths. Then we place each unit
// centered in its allocated slot, and each child gets a dedicated slot
// that's exactly wide enough for its own subtree. No collisions ever,
// because every subtree has its own horizontal band.
//
// A "unit" is either a single person or a couple (two people drawn next
// to each other with COUPLE_GAP between them, forming a heart-joined
// pair). Children of a couple are shared between both partners — they
// only appear in the subtree once, centered under the couple's midpoint.
// ============================================================
function layoutTree(members /* , _unused */) {
  const pos = {};
  if (!members || members.length === 0) {
    return { pos, totalW: 0, totalH: 0 };
  }
  
  const byId = new Map();
  members.forEach(m => byId.set(m.id, m));
  
  // Bidirectional spouse lookup (consistent with the rest of the file).
  const spouseOf = (mem) => {
    if (!mem) return null;
    if (mem.spouse_id) {
      const direct = byId.get(mem.spouse_id);
      if (direct) return direct;
    }
    for (const m of members) {
      if (m.spouse_id === mem.id) return m;
    }
    return null;
  };
  
  // Build "units". A unit is either { kind:'single', a } or { kind:'couple', a, b }.
  // Each person belongs to exactly one unit. We pair people into couples when
  // both partners are in the visible set; unpaired spouses stay single.
  const unitByPersonId = new Map();
  const units = [];
  const consumed = new Set();
  
  members.forEach(m => {
    if (consumed.has(m.id)) return;
    const sp = spouseOf(m);
    if (sp && !consumed.has(sp.id) && byId.has(sp.id)) {
      // Deterministic order within the couple: the one with parents in the
      // visible set goes on the left (the "blood" side). If both or neither
      // have parents here, fall back to original member order.
      const mHasParents = (m.parent_ids || []).some(pid => byId.has(pid));
      const spHasParents = (sp.parent_ids || []).some(pid => byId.has(pid));
      let left = m, right = sp;
      if (spHasParents && !mHasParents) {
        left = sp; right = m;
      }
      const unit = { kind: 'couple', a: left, b: right };
      units.push(unit);
      unitByPersonId.set(left.id, unit);
      unitByPersonId.set(right.id, unit);
      consumed.add(left.id);
      consumed.add(right.id);
    } else {
      const unit = { kind: 'single', a: m };
      units.push(unit);
      unitByPersonId.set(m.id, unit);
      consumed.add(m.id);
    }
  });
  
  // Unit width: single=64, couple=64+15+64=143 (with NODE_R=32, COUPLE_GAP=15).
  const unitWidth = (u) =>
    u.kind === 'couple' ? (NODE_R * 4 + COUPLE_GAP) : (NODE_R * 2);
  
  // Find children of a unit: members whose parent_ids contains either
  // partner of the unit (for couples) or the single member (for singles),
  // AND whose resulting child-unit isn't already owned by a different
  // parent-unit (so we don't double-count). Children are returned as
  // child UNITS, deduped, in the order they appear in `members`.
  const childUnitsOf = (u) => {
    const parentIds = u.kind === 'couple' ? [u.a.id, u.b.id] : [u.a.id];
    const seenUnits = new Set();
    const out = [];
    members.forEach(m => {
      if (!(m.parent_ids || []).some(pid => parentIds.includes(pid))) return;
      const childUnit = unitByPersonId.get(m.id);
      if (!childUnit) return;
      if (seenUnits.has(childUnit)) return;
      seenUnits.add(childUnit);
      out.push(childUnit);
    });
    return out;
  };
  
  // Identify ROOT units: units where neither partner has any parent in the
  // visible member set. These are the top of the rendered tree.
  const isRootUnit = (u) => {
    const partners = u.kind === 'couple' ? [u.a, u.b] : [u.a];
    return !partners.some(p => (p.parent_ids || []).some(pid => byId.has(pid)));
  };
  const rootUnits = units.filter(isRootUnit);
  
  // A child unit can have 2 parents (couple) where each parent is in a
  // DIFFERENT parent-unit. To avoid double-placing, we track which units
  // have been placed already; a child is placed by its "primary" parent
  // (the first parent-unit we encounter that claims it).
  const placedUnits = new Set();
  
  // Compute subtree layout recursively. Returns the subtree's total width.
  // Does NOT place anything yet — placement happens in placeSubtree below.
  const subtreeWidthCache = new Map();
  const computeWidth = (u, inProgress = new Set()) => {
    if (subtreeWidthCache.has(u)) return subtreeWidthCache.get(u);
    if (inProgress.has(u)) {
      // Cycle guard — shouldn't happen in a family tree but be safe.
      return unitWidth(u);
    }
    inProgress.add(u);
    
    const kids = childUnitsOf(u).filter(c => !placedUnits.has(c));
    let w;
    if (kids.length === 0) {
      w = unitWidth(u);
    } else {
      // Sum of children widths + gaps between them
      const kidsWidth = kids.reduce((acc, k, i) => {
        return acc + computeWidth(k, inProgress) + (i > 0 ? SIBLING_GAP : 0);
      }, 0);
      w = Math.max(unitWidth(u), kidsWidth);
    }
    inProgress.delete(u);
    subtreeWidthCache.set(u, w);
    return w;
  };
  
  // Place a unit and its subtree starting at the given left X and y.
  const placeSubtree = (u, leftX, y) => {
    if (placedUnits.has(u)) return;
    placedUnits.add(u);
    
    const w = computeWidth(u);
    const uW = unitWidth(u);
    
    // Place the unit horizontally centered within its allocated slot.
    const unitLeft = leftX + (w - uW) / 2;
    if (u.kind === 'couple') {
      pos[u.a.id] = { x: unitLeft, y };
      pos[u.b.id] = { x: unitLeft + NODE_R * 2 + COUPLE_GAP, y };
    } else {
      pos[u.a.id] = { x: unitLeft, y };
    }
    
    // Place children below, left-to-right, each in their own subtree slot.
    const kids = childUnitsOf(u).filter(c => !placedUnits.has(c));
    if (kids.length === 0) return;
    
    const kidsWidth = kids.reduce((acc, k, i) => {
      return acc + computeWidth(k) + (i > 0 ? SIBLING_GAP : 0);
    }, 0);
    
    // Center the children row horizontally within the parent's slot.
    let childLeft = leftX + (w - kidsWidth) / 2;
    const childY = y + NODE_R * 2 + V_GAP;
    
    kids.forEach(k => {
      const kW = computeWidth(k);
      placeSubtree(k, childLeft, childY);
      childLeft += kW + SIBLING_GAP;
    });
  };
  
  // Lay out each root subtree left-to-right at the top level.
  // Orphan units that aren't reachable from a root (e.g. a grandparent
  // couple with nobody connecting to them) are also treated as roots so
  // they still render somewhere instead of disappearing.
  let rootLeft = NODE_R; // small left margin
  const rootY = NODE_R;
  
  rootUnits.forEach(r => {
    if (placedUnits.has(r)) return;
    const w = computeWidth(r);
    placeSubtree(r, rootLeft, rootY);
    rootLeft += w + FAMILY_GAP;
  });
  
  // Safety net: place any units that didn't get picked up by any root.
  // This can happen with orphan manual entries or broken parent links.
  units.forEach(u => {
    if (placedUnits.has(u)) return;
    const w = computeWidth(u);
    placeSubtree(u, rootLeft, rootY);
    rootLeft += w + FAMILY_GAP;
  });
  
  // Compute total dimensions from placed positions.
  const allX = Object.values(pos).map(p => p.x);
  const allY = Object.values(pos).map(p => p.y);
  const totalW = (allX.length ? Math.max(...allX) : 0) + NODE_R * 2;
  const totalH = (allY.length ? Math.max(...allY) : 0) + NODE_R * 2;
  
  return { pos, totalW, totalH };
}

// Build edges with curved bezier pattern for parent-child connections
function buildEdges(members, pos) {
  const edges = [];
  const couples = identifyCouples(members);

  // Group children by their parent set
  const parentChildGroups = new Map();
  
  members.forEach(child => {
    if (!child.parent_ids || child.parent_ids.length === 0) return;
    
    // Create a key from sorted parent IDs to group siblings
    const parentKey = [...child.parent_ids].sort().join(',');
    
    if (!parentChildGroups.has(parentKey)) {
      parentChildGroups.set(parentKey, {
        parentIds: child.parent_ids,
        children: []
      });
    }
    
    parentChildGroups.get(parentKey).children.push(child);
  });
  
  // Process each parent group
  parentChildGroups.forEach(({ parentIds, children }) => {
    if (children.length === 0) return;
    
    // Find the parent members
    const parents = members.filter(m => parentIds.includes(m.id));
    if (parents.length === 0) return;
    
    // Check if parents are a couple
    const parentCouple = couples.find(c => 
      parents.some(p => p.id === c[0].id) && parents.some(p => p.id === c[1].id)
    );
    
    let parentX, parentY;
    
    if (parentCouple && parents.length === 2) {
      // Couple - use midpoint between them
      const pos1 = pos[parents[0].id];
      const pos2 = pos[parents[1].id];
      if (!pos1 || !pos2) return;
      
      parentX = (pos1.x + pos2.x) / 2;
      parentY = pos1.y;
    } else if (parents.length === 1) {
      // Single parent
      const parentPos = pos[parents[0].id];
      if (!parentPos) return;
      
      parentX = parentPos.x;
      parentY = parentPos.y;
    } else {
      // Multiple parents but not a recognized couple - use midpoint
      const parentPositions = parents.map(p => pos[p.id]).filter(Boolean);
      if (parentPositions.length === 0) return;
      
      const minParentX = Math.min(...parentPositions.map(p => p.x));
      const maxParentX = Math.max(...parentPositions.map(p => p.x));
      parentX = (minParentX + maxParentX) / 2;
      parentY = parentPositions[0].y;
    }
    
    // Get children positions
    const childPositions = children.map(c => pos[c.id]).filter(Boolean);
    if (childPositions.length === 0) return;
    
    const childY = childPositions[0].y;
    
    // CURVED BEZIER PATTERN:
    // Draw individual curved lines from parent to each child (or child-couple midpoint)
    children.forEach(child => {
      const childPos = pos[child.id];
      if (!childPos) return;
      
      // Check if this child is part of a couple
      const childCouple = couples.find(c => c[0].id === child.id || c[1].id === child.id);
      
      let targetX, targetY;
      
      if (childCouple) {
        // Child is in a couple - connect to midpoint between them
        const pos1 = pos[childCouple[0].id];
        const pos2 = pos[childCouple[1].id];
        if (pos1 && pos2) {
          targetX = (pos1.x + pos2.x) / 2;
          targetY = pos1.y;
        } else {
          targetX = childPos.x;
          targetY = childPos.y;
        }
      } else {
        // Single child
        targetX = childPos.x;
        targetY = childPos.y;
      }
      
      // Create curved edge from parent to child
      edges.push({
        type: 'curve',
        x1: parentX,
        y1: parentY + NODE_R,
        x2: targetX,
        y2: targetY - NODE_R
      });
    });
  });
  
  // Spouse connections (simple horizontal line)
  couples.forEach(([p1, p2]) => {
    const pos1 = pos[p1.id];
    const pos2 = pos[p2.id];
    if (!pos1 || !pos2) return;
    
    edges.push({
      type: 'spouse',
      x1: pos1.x + NODE_R,
      y1: pos1.y,
      x2: pos2.x - NODE_R,
      y2: pos2.y
    });
  });
  
  return edges;
}

// Main FamilyTree component
export default function FamilyTree({ family, voters, highlightId, onClickMember, supabase, canSeeSupport = true }) {
  const [showFullTree, setShowFullTree] = useState(false);
  // `expanded` is a set of member IDs whose descendants should be shown.
  // Applies uniformly to siblings, children, grandchildren — any node with
  // a spouse/children can be expanded, and that expansion is recursive.
  const [expanded, setExpanded] = useState([]);
  
  const allMembers = family.members || [];
  
  // Expansion state is the same in both views. Full Tree starts with a wider
  // BASE set (ancestors + aunts/uncles) and the +/- buttons still control
  // drilling into deeper branches (cousins-of-cousins, grandchildren, etc.).
  const effectiveExpanded = expanded;
  
  // Filter members based on view mode + expansion state.
  // Immediate restricts to parents + siblings + children.
  // Full Tree extends the base set to all ancestors (recursive) + aunts/uncles,
  // and keeps the same +/- expand-collapse UX for drilling into branches.
  const members = useMemo(() => {
    return getImmediateFamily(allMembers, highlightId, effectiveExpanded, showFullTree);
  }, [allMembers, highlightId, effectiveExpanded, showFullTree]);
  
  const { pos, totalW, totalH } = useMemo(() => layoutTree(members), [members]);
  const edges = useMemo(() => buildEdges(members, pos), [members, pos]);
  
  // Compute the viewer's bloodline ONCE per render. This drives the 💍 ring:
  // the ring marks people who are NOT in the viewer's bloodline but married
  // to someone who IS, so the ring flips sides correctly when you navigate
  // from one family member's profile to their spouse's profile.
  const bloodline = useMemo(
    () => computeBloodline(allMembers, highlightId),
    [allMembers, highlightId]
  );

  // Paternal vs maternal side for every ancestor. Used by the Full Tree
  // render pass to label grandparent couples so it's obvious which side
  // is which.
  const ancestorSides = useMemo(
    () => computeAncestorSides(allMembers, highlightId, voters),
    [allMembers, highlightId, voters]
  );

  // Direct parents of the viewer — we tag them in ancestorSides for
  // completeness, but skip the badge since their role label (Father/Mother)
  // already conveys the side.
  const directParentIds = useMemo(() => {
    const v = allMembers.find(m => m.voter_id === highlightId);
    return new Set(v ? (v.parent_ids || []) : []);
  }, [allMembers, highlightId]);
  
  // Collect every photo_url that needs to be displayed for the visible
  // members (both the voter-linked photo and the member's own manual photo)
  // so we can resolve them all in a single batched signed-URL call instead
  // of one round trip per member. The result is a Map<storedValue → URL>.
  const photoValues = useMemo(() => {
    const seen = new Set();
    const list = [];
    members.forEach(m => {
      const v = voters.find(x => {
        const numId = typeof m.voter_id === 'string' ? parseInt(m.voter_id, 10) : m.voter_id;
        return x.id === numId || x.id === m.voter_id;
      });
      // Family-member's own photo (uploaded in tree edit modal) takes priority,
      // falling back to the linked voter's profile photo.
      const url = m.photo_url || v?.photo_url;
      if (url && !seen.has(url)) {
        seen.add(url);
        list.push(url);
      }
    });
    return list;
  }, [members, voters]);
  
  const photoUrlMap = usePhotoUrls(supabase, photoValues);
  
  // Find current member for highlight / viewer role checks.
  const currentMember = allMembers.find(m => m.voter_id === highlightId);
  
  // Precompute the set of ids whose + button should be hidden.
  // Rationale: the + button expands a member's spouse + children. The
  // viewer's own spouse and children are ALREADY visible by default in
  // Immediate Family view, so expansion has nothing to add for:
  //   - the viewer themselves (their family is already the default view)
  //   - the viewer's spouse (same children as the viewer — redundant)
  //   - every ancestor (parents, grandparents, ...) — the chain down
  //     from an ancestor to the viewer is already fully rendered
  const hideExpandIds = (() => {
    const hidden = new Set();
    if (!currentMember) return hidden;
    hidden.add(currentMember.id);
    
    // Viewer's spouse — bidirectional lookup to handle one-directional
    // spouse_id in the DB.
    let viewerSpouse = null;
    if (currentMember.spouse_id) {
      viewerSpouse = allMembers.find(m => m.id === currentMember.spouse_id) || null;
    }
    if (!viewerSpouse) {
      viewerSpouse = allMembers.find(m => m.spouse_id === currentMember.id) || null;
    }
    if (viewerSpouse) hidden.add(viewerSpouse.id);
    
    // All ancestors — walk up via parent_ids recursively, with cycle guard.
    // `hidden` doubles as the visited set. The viewer itself is already in
    // hidden, but we still need to traverse their parent_ids on the first
    // call — so cycle-check each pid BEFORE recursing, not on entry.
    const walkUp = (id) => {
      const mem = allMembers.find(m => m.id === id);
      if (!mem) return;
      (mem.parent_ids || []).forEach(pid => {
        if (hidden.has(pid)) return;
        hidden.add(pid);
        walkUp(pid);
      });
    };
    walkUp(currentMember.id);
    
    return hidden;
  })();
  
  // Should this member get a +/- button? Yes if they have at least one
  // child or a spouse (someone to reveal via expansion). Hidden for the
  // viewer, the viewer's spouse, and every ancestor (their descendants
  // down to the viewer are already on screen). In Full Tree the same
  // rules apply — siblings, aunts/uncles, cousins, children all stay
  // expandable so users can drill into deeper branches.
  const canExpand = (m) => {
    if (hideExpandIds.has(m.id)) return false;
    // Has a spouse? (bidirectional check)
    const hasSpouse = !!m.spouse_id || allMembers.some(x => x.spouse_id === m.id);
    // Has children?
    const hasChildren = allMembers.some(c => (c.parent_ids || []).includes(m.id));
    return hasSpouse || hasChildren;
  };
  
  // Robust getV that handles type mismatches
  const getV = (id) => {
    if (!id) return null;
    const numId = typeof id === 'string' ? parseInt(id) : id;
    const found = voters.find(v => v.id === numId || v.id === id);
    return found || null;
  };
  
  const PAD = 40;

  // ---------------------------------------------------------------
  // MODAL STATE + BEHAVIOR
  // ---------------------------------------------------------------
  // isMobile uses matchMedia (not innerWidth) to stay stable on iOS Safari,
  // where the auto-hiding URL bar fires resize events on scroll and can
  // briefly report an innerWidth that flips our breakpoint.
  // Declared FIRST because the effects below depend on it.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  // Esc key closes the modal. Body scroll is locked while open so the
  // background page can't scroll behind the overlay.
  // On mobile we never open the modal — Full Tree is shown inline — so we
  // skip these effects entirely when isMobile is true.
  useEffect(() => {
    if (!showFullTree || isMobile) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowFullTree(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showFullTree, isMobile]);

  // Track viewport size so we can (a) render different modal chrome on
  // mobile vs desktop and (b) compute an auto fit-to-screen scale for
  // the SVG so wide trees shrink to fit instead of forcing scroll.
  // Only needed when the DESKTOP modal is actually open — mobile never
  // shows the modal, so the viewport math is wasted work there.
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1024,
    h: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  useEffect(() => {
    if (!showFullTree || isMobile) return;
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [showFullTree, isMobile]);


  // Modal inner dimensions (desktop = 95vw/vh with chrome; mobile = full).
  // Subtract header + internal padding to get the actual area available
  // to the SVG, so fit-scale is accurate.
  const HEADER_H = isMobile ? 52 : 56;
  const modalW = isMobile ? viewport.w : Math.floor(viewport.w * 0.95);
  const modalH = isMobile ? viewport.h : Math.floor(viewport.h * 0.95);
  const innerW = modalW - (isMobile ? 0 : 24); // side padding inside body
  const innerH = modalH - HEADER_H - (isMobile ? 0 : 24);

  // Natural SVG size (what the inline view would render at 1x).
  const svgW = totalW + PAD * 2;
  const svgH = totalH + PAD * 2;

  // Fit-to-screen scale: shrink so the whole tree fits the modal width.
  // Never upscale above 1x (small trees stay their natural size and
  // center). Cap the MIN scale at 0.45 so text stays readable — below
  // that, we stop scaling and let the user scroll/pan instead.
  const MIN_SCALE = 0.45;
  const fitScaleRaw = Math.min(1, innerW / svgW, innerH / svgH);
  const fitScale = Math.max(fitScaleRaw, MIN_SCALE);
  // If the natural fit is below MIN_SCALE, the scaled tree will still
  // overflow the modal — so the body needs to be scrollable.
  const needsScroll = fitScaleRaw < MIN_SCALE;

  // ---------------------------------------------------------------
  // TREE SVG — extracted once so it can render inline OR in the modal
  // without duplicating ~250 lines of rendering code.
  // ---------------------------------------------------------------
  const treeSvg = (
    <svg
      width={svgW}
      height={svgH}
      viewBox={`0 0 ${svgW} ${svgH}`}
      style={{ display: 'block', margin: '0 auto' }}
    >
          <defs>
            <filter id="hg">
              <feGaussianBlur stdDeviation="3" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
        
        {/* Draw edges - curved lines for parent-child, straight for spouse */}
        {edges.map((e, i) => {
          if (e.type === 'spouse') {
            // Spouse: double line with heart (UNCHANGED)
            return (
              <g key={i}>
                <line x1={e.x1 + PAD} y1={e.y1 + PAD - 3} x2={e.x2 + PAD} y2={e.y2 + PAD - 3} stroke="var(--fam-spouse)" strokeWidth={1.2} opacity={0.5} />
                <line x1={e.x1 + PAD} y1={e.y1 + PAD + 3} x2={e.x2 + PAD} y2={e.y2 + PAD + 3} stroke="var(--fam-spouse)" strokeWidth={1.2} opacity={0.5} />
                <text x={(e.x1 + e.x2) / 2 + PAD} y={e.y1 + PAD - 10} textAnchor="middle" fontSize={9} fill="var(--fam-spouse)" opacity={0.7}>♥</text>
              </g>
            );
          } else {
            // Parent-child: curved bezier path
            const x1 = e.x1 + PAD;
            const y1 = e.y1 + PAD;
            const x2 = e.x2 + PAD;
            const y2 = e.y2 + PAD;
            
            // Calculate control points for smooth S-curve
            const verticalDist = Math.abs(y2 - y1);
            const controlOffset = verticalDist * 0.4; // 40% of vertical distance
            
            // Control points: slightly below start, slightly above end
            const cx1 = x1;
            const cy1 = y1 + controlOffset;
            const cx2 = x2;
            const cy2 = y2 - controlOffset;
            
            // Create SVG path with cubic bezier curve
            const pathData = `M ${x1},${y1} C ${cx1},${cy1} ${cx2},${cy2} ${x2},${y2}`;
            
            return (
              <path
                key={i}
                d={pathData}
                fill="none"
                stroke="var(--border-strong)"
                strokeWidth={1.5}
                opacity={0.7}
              />
            );
          }
        })}
        
        {/* Draw nodes */}
        {members.map(m => {
          const p = pos[m.id];
          if (!p) {
            // SAFETY NET: surface dropped members instead of silently hiding them
            console.warn('⚠️ FamilyTree: member has no position, skipping render:', m.name, m.id);
            return null;
          }
          
          const voter = getV(m.voter_id);
          const sc = (voter && canSeeSupport) ? SC[voter.support] : null;
          const dc = sc ? sc.dot : 'var(--border-strong)';
          const isHL = voter && voter.id === highlightId;
          const cx = p.x + PAD;
          const cy = p.y + PAD;
          const init = (m.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
          
          // Married-in relative to THE VIEWER (ring flips between profiles)
          const isMarriedInMember = isMarriedIn(m, allMembers, bloodline);
          
          return (
            <g key={m.id}>
              {/* Main node - clickable */}
              <g 
                onClick={() => { if (showFullTree && !isMobile) setShowFullTree(false); onClickMember(m, voter); }} 
                style={{ cursor: voter ? 'pointer' : 'default' }}
              >
                {/* Highlight glow */}
                {isHL && <circle cx={cx} cy={cy} r={NODE_R + 8} fill={dc} opacity={0.15} filter="url(#hg)" />}
                
                {/* Outer ring */}
                <circle cx={cx} cy={cy} r={NODE_R + 3} fill="none" stroke={dc} strokeWidth={isHL ? 2 : 1} opacity={isHL ? 0.9 : 0.25} />
                
                {/* Main node */}
                <circle cx={cx} cy={cy} r={NODE_R} fill="var(--surface)" stroke={dc} strokeWidth={isHL ? 2.5 : 1.8} />
                
                {/* Photo or initials.
                    Priority: linked voter's photo > manual member's own photo > initials.
                    The stored value is either a Storage path (resolved to a
                    signed URL via photoUrlMap), a base64 data URI (legacy),
                    or null. While the URL map is loading, falls back to
                    initials gracefully. */}
                {(() => {
                  // Prefer the family-member's own uploaded photo, fall back to the linked voter's.
                  const rawPhoto = m.photo_url || voter?.photo_url;
                  const resolvedPhoto = rawPhoto ? photoUrlMap.get(rawPhoto) : null;
                  if (resolvedPhoto) {
                    return (
                      <>
                        <image href={resolvedPhoto} x={cx - NODE_R} y={cy - NODE_R} width={NODE_R * 2} height={NODE_R * 2} clipPath={`url(#c${m.id})`} preserveAspectRatio="xMidYMid slice" />
                        <clipPath id={`c${m.id}`}>
                          <circle cx={cx} cy={cy} r={NODE_R} />
                        </clipPath>
                      </>
                    );
                  }
                  return (
                    <text x={cx} y={cy + 6} textAnchor="middle" fontSize={14} fontWeight="700" fontFamily="'Cormorant Garamond',serif" fill={sc ? sc.text : 'var(--link)'}>
                      {init}
                    </text>
                  );
                })()}
                
                {/* Dim overlay for unlinked members WITHOUT a custom photo.
                    A manual member with their own photo should look normal. */}
                {!voter && !m.photo_url && <circle cx={cx} cy={cy} r={NODE_R} fill="#000" opacity={0.35} />}
                
                {/* Support dot */}
                {sc && <circle cx={cx + NODE_R - 5} cy={cy + NODE_R - 5} r={5} fill={sc.dot} stroke="var(--bg)" strokeWidth={1.5} />}
                
                {/* Married-in indicator (heart icon) - top-left corner */}
                {isMarriedInMember && (
                  <g>
                    <circle
                      cx={cx - NODE_R + 8}
                      cy={cy - NODE_R + 8}
                      r={7}
                      fill="var(--border)"
                      stroke="var(--fam-spouse)"
                      strokeWidth={1.5}
                    />
                    <path
                      d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"
                      fill="var(--fam-spouse)"
                      transform={`translate(${cx - NODE_R + 8}, ${cy - NODE_R + 8}) scale(0.4) translate(-12, -12)`}
                    />
                  </g>
                )}

                {/* Key influencer badge (star icon) - top-right corner */}
                {voter?.influencer && (
                  <g>
                    <circle
                      cx={cx + NODE_R - 8}
                      cy={cy - NODE_R + 8}
                      r={7}
                      fill="var(--border)"
                      stroke="var(--fam-influencer)"
                      strokeWidth={1.5}
                    />
                    <path
                      d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2z"
                      fill="var(--fam-influencer)"
                      transform={`translate(${cx + NODE_R - 8}, ${cy - NODE_R + 8}) scale(0.35) translate(-12, -12)`}
                    />
                  </g>
                )}

                {/* Name */}
                <text x={cx} y={cy + NODE_R + 14} textAnchor="middle" fontSize={9.5} fill="var(--text)" fontFamily="'DM Mono',monospace" fontWeight="500">
                  {(m.name || '?').slice(0, 13)}{(m.name || '?').length > 13 ? '…' : ''}
                </text>
              </g>
            </g>
          );
        })}
        
        {/* Expand/collapse buttons rendered in a SEPARATE pass AFTER all nodes,
            so they're always on top and never occluded by a neighboring spouse
            or sibling node. One button per FAMILY UNIT (couple or single) —
            not one per individual — because a couple shares their children
            and should act as a single expandable group. */}
        {(() => {
          // Build couple index so we can dedupe: only draw a button for the
          // LEFT partner of a couple (the one with the smaller x), centered
          // between the two partners. Singles get a button at their own x.
          const buttons = [];
          const handledCouplePartners = new Set();
          
          // Bidirectional spouse lookup in the visible set
          const findSp = (mem) => {
            if (!mem) return null;
            if (mem.spouse_id) {
              const d = members.find(x => x.id === mem.spouse_id);
              if (d) return d;
            }
            return members.find(x => x.spouse_id === mem.id) || null;
          };
          
          members.forEach(m => {
            if (!canExpand(m)) return;
            if (handledCouplePartners.has(m.id)) return;
            const p = pos[m.id];
            if (!p) return;
            
            const sp = findSp(m);
            const spInView = sp && members.some(x => x.id === sp.id);
            const spPos = spInView ? pos[sp.id] : null;
            
            let btnX, toggleIds;
            if (spPos) {
              // Couple — one button centered between the two node centers.
              // Mark both partners as handled so we don't draw a second button.
              btnX = ((p.x + NODE_R) + (spPos.x + NODE_R)) / 2 + PAD;
              handledCouplePartners.add(m.id);
              handledCouplePartners.add(sp.id);
              toggleIds = [m.id, sp.id];
            } else {
              // Single — button directly below the node center.
              btnX = p.x + NODE_R + PAD;
              toggleIds = [m.id];
            }
            
            const btnY = p.y + PAD + NODE_R + 28;
            // Button is "open" if EITHER partner is in the expanded set.
            // Clicking toggles BOTH partners so the state stays in sync
            // (the walker will pull in the spouse+kids either way).
            const isOpen = toggleIds.some(id => expanded.includes(id));
            
            buttons.push({ key: toggleIds.join('|'), btnX, btnY, isOpen, toggleIds });
          });
          
          return buttons.map(({ key, btnX, btnY, isOpen, toggleIds }) => (
            <g
              key={`btn-${key}`}
              onClick={(e) => {
                e.stopPropagation();
                // Toggle: if currently open, remove all toggle ids; if
                // currently closed, add them. Keeps both partners in sync.
                if (isOpen) {
                  setExpanded(prev => prev.filter(id => !toggleIds.includes(id)));
                } else {
                  setExpanded(prev => [...prev, ...toggleIds.filter(id => !prev.includes(id))]);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={btnX}
                cy={btnY}
                r={9}
                fill={isOpen ? '#10b981' : 'var(--surface)'}
                stroke={isOpen ? '#22c55e' : 'var(--link)'}
                strokeWidth={1.5}
              />
              <text
                x={btnX}
                y={btnY + 4}
                textAnchor="middle"
                fontSize={12}
                fontWeight="700"
                fill={isOpen ? '#fff' : 'var(--link)'}
              >
                {isOpen ? '−' : '+'}
              </text>
            </g>
          ));
        })()}

        {/* Paternal / Maternal badges — Full Tree only. One badge per
            grandparent (and above) couple. Direct parents are skipped
            since their Father/Mother role already conveys the side. */}
        {showFullTree && (() => {
          const badges = [];
          const handled = new Set();
          const findSp = (mem) => {
            if (!mem) return null;
            if (mem.spouse_id) {
              const d = members.find(x => x.id === mem.spouse_id);
              if (d) return d;
            }
            return members.find(x => x.spouse_id === mem.id) || null;
          };
          members.forEach(m => {
            if (handled.has(m.id)) return;
            const side = ancestorSides.get(m.id);
            if (!side) return;
            if (directParentIds.has(m.id)) return;
            const p = pos[m.id];
            if (!p) return;

            const sp = findSp(m);
            const spInView = sp && members.some(x => x.id === sp.id);
            const spPos = spInView ? pos[sp.id] : null;

            let badgeX;
            if (spPos) {
              badgeX = ((p.x + NODE_R) + (spPos.x + NODE_R)) / 2 + PAD;
              handled.add(m.id);
              handled.add(sp.id);
            } else {
              badgeX = p.x + NODE_R + PAD;
              handled.add(m.id);
            }
            // Place the badge ABOVE the node within the SVG padding so it
            // cannot be clipped by the viewBox even when the topmost
            // ancestor sits at y=0. PAD (the SVG margin) is comfortably
            // larger than the badge height, so this is always in-bounds.
            const badgeY = Math.max(p.y + PAD - NODE_R - 18, 12);
            badges.push({ key: m.id, badgeX, badgeY, side });
          });

          return badges.map(({ key, badgeX, badgeY, side }) => {
            const isPaternal = side === 'paternal';
            const stroke = isPaternal ? 'var(--fam-paternal-border)' : 'var(--fam-maternal-border)';
            const fill = isPaternal ? 'var(--fam-paternal-bg)' : 'var(--fam-maternal-bg)';
            const text = isPaternal ? 'var(--fam-paternal-text)' : 'var(--fam-maternal-text)';
            const label = isPaternal ? 'PATERNAL' : 'MATERNAL';
            const w = 64;
            const h = 14;
            return (
              <g key={`side-${key}`} pointerEvents="none">
                <rect
                  x={badgeX - w / 2}
                  y={badgeY - h / 2}
                  width={w}
                  height={h}
                  rx={4}
                  ry={4}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={1}
                  opacity={0.95}
                />
                <text
                  x={badgeX}
                  y={badgeY + 3}
                  textAnchor="middle"
                  fontSize={8}
                  fontWeight="700"
                  fontFamily="'DM Mono',monospace"
                  fill={text}
                >
                  {label}
                </text>
              </g>
            );
          });
        })()}
      </svg>
  );

  return (
    <div>
      {/* Two buttons side-by-side: Immediate vs Full Tree.
          On desktop: Immediate is always inline, Full Tree opens a modal.
          On mobile:  Both views are inline — the Full Tree button acts as
                      a true toggle alongside Immediate. Active state is
                      highlighted either way. */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 16,
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setShowFullTree(false)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            borderRadius: 7,
            border: !showFullTree ? '1px solid var(--link)' : '1px solid var(--border)',
            background: !showFullTree ? 'var(--border)' : 'transparent',
            color: !showFullTree ? 'var(--link)' : 'var(--text-3)',
            cursor: 'pointer',
            fontSize: 10,
            fontWeight: 500,
            fontFamily: "'DM Mono', monospace"
          }}>
          <Icon name="user" size={12} /> Immediate Family
        </button>
        <button
          onClick={() => setShowFullTree(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            borderRadius: 7,
            border: showFullTree ? '1px solid var(--link)' : '1px solid var(--border)',
            background: showFullTree ? 'var(--border)' : 'transparent',
            color: showFullTree ? 'var(--link)' : 'var(--text-3)',
            cursor: 'pointer',
            fontSize: 10,
            fontWeight: 500,
            fontFamily: "'DM Mono', monospace"
          }}>
          <Icon name="users" size={12} /> Full Family Tree
        </button>
      </div>

      {/* Inline tree shows the Immediate Family view (expanded state respected).
          width:100% + max-width:100% + overflow-x:auto forces wide trees to
          scroll INSIDE this container rather than expanding the whole page
          wider than the viewport. Without the width constraint, the div
          auto-sizes to its SVG child and the page blows out horizontally. */}
      <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {treeSvg}
      </div>

      {/* Fullscreen modal for Full Family Tree view — DESKTOP ONLY.
          On mobile, the inline tree above shows the full tree with horizontal
          scroll inside its container. No modal needed. */}
      {showFullTree && !isMobile && (
        <div
          onClick={() => setShowFullTree(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: isMobile ? 0 : '2.5vh 2.5vw',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: modalW,
              height: modalH,
              background: 'var(--bg)',
              border: isMobile ? 'none' : '1px solid var(--border)',
              borderRadius: isMobile ? 0 : 12,
              boxShadow: isMobile ? 'none' : '0 24px 60px rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              height: HEADER_H,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '0 12px 0 16px' : '0 20px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--surface)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                color: 'var(--text)',
                fontSize: isMobile ? 13 : 14,
                fontWeight: 600,
                fontFamily: "'DM Mono', monospace",
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                <Icon name="users" size={16} /> Full Family Tree
              </div>
              <button
                onClick={() => setShowFullTree(false)}
                aria-label="Close"
                style={{
                  width: isMobile ? 40 : 32,
                  height: isMobile ? 40 : 32,
                  borderRadius: isMobile ? 10 : 8,
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text-2)',
                  cursor: 'pointer',
                  fontSize: isMobile ? 22 : 18,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>

            {/* Body — scaled tree, scrollable both directions if needed.
                Align to top so short trees don't float in the middle of a
                tall phone screen (they anchor to the top, below the header). */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: isMobile ? 8 : 12,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              WebkitOverflowScrolling: 'touch',
            }}>
              <div style={{
                width: svgW * fitScale,
                height: svgH * fitScale,
                flexShrink: 0,
              }}>
                <div style={{
                  width: svgW,
                  height: svgH,
                  transform: `scale(${fitScale})`,
                  transformOrigin: 'top left',
                }}>
                  {treeSvg}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
